// Contracts (parts/Contracts.body): the Contracts Register's shape, one row per contract,
// the signed document beside it (D33, D100); Legal L1 by default. 0 rows until the register
// is given (milestone 3 open item 1), and the heading says so from the count.
import { useState } from 'react';
import { records, type ContractFilter } from '../api.js';
import { formatDate } from '../labels.js';
import { Count, Empty, Facet, Failed, Foot, Heading, Loading, orText, Pill, RowLink, useLoad, type PageProps } from './shared.js';

export function Contracts({ store, open }: PageProps) {
  const [filter, setFilter] = useState<ContractFilter>('all');
  const [product, setProduct] = useState('');
  const [state] = useLoad(() => records.contracts(store.tenantId, { filter, product }), [store.tenantId, filter, product]);
  const counts = state.kind === 'ready' ? state.data.counts : null;
  return (
    <div className="recs">
      <Heading title="Contracts" line={<>
        <Count n={counts?.all}>on record</Count>
        <Count n={counts?.live}>live</Count>
        <Count n={counts?.ending90}>ending within 90 days</Count>
      </>} />
      <div className="filters">
        <div className="chips">
          <span className="chips-l">Show</span>
        <Pill on={filter === 'all'} onClick={() => setFilter('all')}>All</Pill>
        <Pill on={filter === 'live'} onClick={() => setFilter('live')} n={counts?.live}>Live</Pill>
        <Pill on={filter === 'ending_90'} onClick={() => setFilter('ending_90')} n={counts?.ending90}>Ending within 90 days</Pill>
        <Pill on={filter === 'auto_renew'} onClick={() => setFilter('auto_renew')} n={counts?.autoRenew}>Auto-renewing</Pill>
        <Pill on={filter === 'in_notice'} onClick={() => setFilter('in_notice')} n={counts?.inNotice}>In notice</Pill>
        <Facet label="By product" value={product} options={state.kind === 'ready' ? state.data.products : []} onChange={setProduct} />
        </div>
      </div>
      {state.kind === 'loading' ? <Loading what="the contracts" /> : state.kind === 'error' ? <Failed message={state.message} /> : (
        <div className="scroll"><table className="rec">
          <thead><tr><th>Counterparty</th><th>IGM deal id</th><th>Product</th><th>Competition</th><th>Status, as registered</th><th>Commenced</th><th>Ends</th><th>Term</th><th>Auto-renew</th><th className="num">Notice, days</th><th>Document</th></tr></thead>
          <tbody>
            {state.data.contracts.length === 0
              ? <Empty cols={11} head="No contract under that." says="Choose another filter, or All. Contracts come from the register, with the signed document read from SharePoint beside each one." />
              : null}
            {state.data.contracts.map((c) => (
              <tr key={c.id} className="row-open" onClick={() => open(`/contracts/${c.id}`)}>
                <td><b><RowLink className="dotted" to={`/companies/${c.counterpartyId}`} open={open}>{c.counterpartyName}</RowLink></b>{c.clientAlias ? <> <span className="note">{c.clientAlias}</span></> : null}</td>
                <td><RowLink to={`/contracts/${c.id}`} open={open}>{orText(c.igmDealId)}</RowLink></td>
                <td>{orText(c.product)}</td>
                <td>{orText(c.competition)}</td>
                <td>{orText(c.registerStatus)}</td>
                <td>{c.commencementDate ? formatDate(c.commencementDate) : ''}</td>
                <td className="when">{c.endDate ? formatDate(c.endDate) : ''}{c.daysToEnd !== null && c.daysToEnd >= 0 && c.daysToEnd <= 90 ? <><br /><span className={c.daysToEnd <= 14 ? 'days soon' : 'days'}>{c.daysToEnd} days</span></> : null}</td>
                <td>{orText(c.initialTerm)}</td>
                <td>{c.autoRenew === null ? '' : c.autoRenew ? 'yes' : 'no'}</td>
                <td className="num">{c.noticePeriodDays ?? ''}</td>
                <td>{c.document ? `${c.document.subject ?? 'signed document'} ↗` : <span className="note">No document filed</span>}</td>
              </tr>
            ))}
{/* BUGS.md D107: "N shown of" every row the pill, product and company match, as the server counts them. */}
<Foot cols={11} offset={0} limit={state.data.contracts.length} total={state.data.total ?? counts?.all ?? state.data.contracts.length} onPage={null} />
          </tbody>
        </table></div>
      )}
    </div>
  );
}
